import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const FILE_ATTACH_COMPANION_ID = '29494837-db45-40c7-81d2-a6440fef829d';

function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [attachedFile, setAttachedFile] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [loading, setLoading] = useState(false);
  const [companion, setCompanion] = useState(null);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [audioProgress, setAudioProgress] = useState({});
  const [audioDurations, setAudioDurations] = useState({});
  const [generatingAudio, setGeneratingAudio] = useState({});
  const [activeMenu, setActiveMenu] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editText, setEditText] = useState('');
  const [favoriteModal, setFavoriteModal] = useState(null);
  const [favoriteNote, setFavoriteNote] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const currentAudioRef = useRef(null);
  const pressTimerRef = useRef(null);
  const { companionId, threadId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const bottomRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    fetchCompanion();
  }, []);

  const prevLengthRef = useRef(0);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 140) + 'px';
    }
  }, [input]);

  useEffect(() => {
    if (isLoadingMoreRef.current) return;
    if (messages.length > prevLengthRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevLengthRef.current = messages.length;
  }, [messages]);

  const fetchCompanion = async () => {
    try {
      const res = await axios.get(`${API}/companions?token=${token}`);
      const found = res.data.companions.find(c => c.id === companionId);
      setCompanion(found);
    } catch(err) { console.log(err); }
  };

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/messages/${threadId}?token=${token}&limit=50`);
      setMessages(res.data.messages);
      setHasMore(res.data.has_more);
      setTotalCount(res.data.total_count || 0);
    } catch(err) { console.log(err); }
  };

  const loadMoreMessages = async () => {
    if (loadingMore || !hasMore || messages.length === 0) return;
    setLoadingMore(true);
    const container = messagesContainerRef.current;
    const scrollHeightBefore = container ? container.scrollHeight : 0;
    try {
      const oldestMessageId = messages[0].message_id;
      const res = await axios.get(
        `${API}/messages/${threadId}?token=${token}&limit=50&before_id=${oldestMessageId}`
      );
      const olderMessages = res.data.messages;
      setHasMore(res.data.has_more);
      isLoadingMoreRef.current = true;
      setMessages(prev => [...olderMessages, ...prev]);
      setTimeout(() => {
        if (container) {
          const scrollHeightAfter = container.scrollHeight;
          container.scrollTop = scrollHeightAfter - scrollHeightBefore;
        }
        requestAnimationFrame(() => { isLoadingMoreRef.current = false; });
      }, 10);
    } catch(err) {
      console.log(err);
      isLoadingMoreRef.current = false;
    }
    setLoadingMore(false);
  };

  const toLocalDate = (timestamp) => {
    if (!timestamp) return new Date();
    const ts = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    return new Date(ts);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = toLocalDate(timestamp);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = toLocalDate(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'today';
    if (date.toDateString() === yesterday.toDateString()) return 'yesterday';
    if (date.getFullYear() === today.getFullYear()) {
      return date.toLocaleDateString([], { month: 'long', day: 'numeric' });
    }
    return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const shouldShowDateDivider = (messages, index) => {
    if (index === 0) return true;
    const curr = toLocalDate(messages[index].timestamp);
    const prev = toLocalDate(messages[index - 1].timestamp);
    return curr.toDateString() !== prev.toDateString();
  };

  const stopAudio = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    setPlayingIndex(null);
  };

  const playAudioUrl = async (audioUrl, index) => {
    if (playingIndex === index) { stopAudio(); return; }
    stopAudio();
    try {
      setPlayingIndex(index);
      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;
      audio.addEventListener('loadedmetadata', () => {
        setAudioDurations(prev => ({ ...prev, [index]: audio.duration }));
      });
      audio.addEventListener('timeupdate', () => {
        setAudioProgress(prev => ({ ...prev, [index]: audio.currentTime }));
      });
      audio.addEventListener('ended', () => {
        setPlayingIndex(null);
        setAudioProgress(prev => ({ ...prev, [index]: 0 }));
        currentAudioRef.current = null;
      });
      await audio.play();
    } catch(err) {
      console.log('audio error:', err);
      setPlayingIndex(null);
    }
  };

  const handlePlay = async (message, index) => {
    if (playingIndex === index) { stopAudio(); return; }
    if (message.audio_url) { playAudioUrl(message.audio_url, index); return; }
    if (!message.message_id) return;
    try {
      setGeneratingAudio(prev => ({ ...prev, [index]: true }));
      const res = await axios.post(`${API}/audio/generate/${message.message_id}?token=${token}`);
      const audio_url = res.data.audio_url;
      setMessages(prev => prev.map((m, i) => i === index ? { ...m, audio_url } : m));
      setGeneratingAudio(prev => ({ ...prev, [index]: false }));
      playAudioUrl(audio_url, index);
    } catch(err) {
      console.log('generate error:', err);
      setGeneratingAudio(prev => ({ ...prev, [index]: false }));
    }
  };

  const scrub = (index, e) => {
    if (!currentAudioRef.current || playingIndex !== index) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
    currentAudioRef.current.currentTime = pct * (audioDurations[index] || 0);
  };

  const shareAudio = async (message, index) => {
    let audioUrl = message.audio_url;
    if (!audioUrl && message.message_id) {
      try {
        setGeneratingAudio(prev => ({ ...prev, [index]: true }));
        const res = await axios.post(`${API}/audio/generate/${message.message_id}?token=${token}`);
        audioUrl = res.data.audio_url;
        setMessages(prev => prev.map((m, i) => i === index ? { ...m, audio_url: audioUrl } : m));
        setGeneratingAudio(prev => ({ ...prev, [index]: false }));
      } catch(err) {
        setGeneratingAudio(prev => ({ ...prev, [index]: false }));
        return;
      }
    }
    if (!audioUrl) return;
    try {
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      const file = new File([blob], `caleb-${index}.mp3`, { type: 'audio/mpeg' });
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'caleb voice message' });
      } else {
        window.open(audioUrl, '_blank');
      }
    } catch(err) {
      console.log('share error:', err);
    }
  };

  const formatTime = (secs) => {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const scrollRef = useRef(null);

  const handlePressStart = (index) => {
    pressTimerRef.current = setTimeout(() => {
      const messagesEl = document.getElementById('messages-container');
      if (messagesEl) scrollRef.current = messagesEl.scrollTop;
      setActiveMenu(index);
    }, 500);
  };

  const handlePressEnd = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };

  const closeMenu = () => {
    setActiveMenu(null);
    setTimeout(() => {
      const messagesEl = document.getElementById('messages-container');
      if (messagesEl && scrollRef.current !== null) {
        messagesEl.scrollTop = scrollRef.current;
      }
    }, 10);
  };

  const deleteMessage = async (message, index) => {
    closeMenu();
    if (message.message_id) {
      try {
        await axios.delete(`${API}/messages/${message.message_id}?token=${token}`);
      } catch(err) { console.log('delete error:', err); }
    }
    setMessages(prev => prev.filter((_, i) => i !== index));
    setTotalCount(prev => prev - 1);
  };

  const startEdit = (message, index) => {
    closeMenu();
    setEditingIndex(index);
    setEditText(message.content);
  };

  const saveEdit = async (message, index) => {
    if (message.message_id) {
      try {
        await axios.put(`${API}/messages/${message.message_id}?token=${token}`, { content: editText });
      } catch(err) { console.log('edit error:', err); }
    }
    setMessages(prev => prev.map((m, i) => i === index ? { ...m, content: editText } : m));
    setEditingIndex(null);
    setEditText('');
  };

  const branchThread = async (message, index) => {
    closeMenu();
    if (!message.message_id) return;
    try {
      const res = await axios.post(
        `${API}/threads/${threadId}/branch/${message.message_id}?token=${token}`
      );
      navigate(`/chat/${companionId}/${res.data.thread_id}`);
    } catch(err) { console.log('branch error:', err); }
  };

  const reroll = async (message, index) => {
    closeMenu();
    if (!message.message_id) return;
    setMessages(prev => prev.map((m, i) => i === index ? { ...m, content: '✨ rerolling…' } : m));
    try {
      const res = await axios.post(`${API}/messages/${message.message_id}/reroll?token=${token}`);
      setMessages(prev => prev.map((m, i) => i === index ? {
        ...m,
        content: res.data.reply,
        audio_url: res.data.audio_url,
        message_id: res.data.message_id
      } : m));
    } catch(err) {
      console.log('reroll error:', err);
      setMessages(prev => prev.map((m, i) => i === index ? { ...m, content: message.content } : m));
    }
  };

  const openFavoriteModal = (message, index) => {
    closeMenu();
    setFavoriteModal({ message, index });
    setFavoriteNote(message.favorite_note || '');
  };

  const saveFavorite = async (isFavorite) => {
    if (!favoriteModal) return;
    const { message, index } = favoriteModal;
    if (!message.message_id) return;
    try {
      await axios.put(`${API}/messages/${message.message_id}/favorite?token=${token}`, {
        token, is_favorite: isFavorite, favorite_note: favoriteNote,
      });
      setMessages(prev => prev.map((m, i) => i === index ? {
        ...m, is_favorite: isFavorite, favorite_note: favoriteNote,
      } : m));
    } catch(err) { console.log('favorite error:', err); }
    setFavoriteModal(null);
    setFavoriteNote('');
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setRecording(true);
    } catch (err) {
      console.log('mic error:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const toggleRecording = () => {
    if (recording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setTranscribing(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result.split(',')[1];
        try {
          const res = await axios.post(`${API}/transcribe`, {
            token,
            audio_base64: base64,
          });
          const text = res.data.text || '';
          const autoSend = localStorage.getItem(`voiceAutoSend_${companionId}`) === 'true';
          if (autoSend && text.trim() && !loading) {
            const combined = (input ? input + ' ' : '') + text;
            sendMessage(combined);
          } else {
            setInput(prev => (prev ? prev + ' ' : '') + text);
          }
        } catch (err) {
          console.log('transcribe error:', err);
        }
        setTranscribing(false);
      };
      reader.readAsDataURL(audioBlob);
    } catch (err) {
      console.log('transcribe error:', err);
      setTranscribing(false);
    }
  };

  const handleFileAttach = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setAttachedFile({ name: file.name, content: ev.target.result }); };
    reader.readAsText(file);
    e.target.value = '';
  };

  const sendMessage = async (overrideText) => {
    const textToUse = overrideText !== undefined ? overrideText : input;
    if ((!textToUse.trim() && !attachedFile) || loading) return;
    let messageToSend = textToUse;
    if (attachedFile) {
      messageToSend = `[Attached file: ${attachedFile.name}]\n\n${attachedFile.content}\n\n${textToUse}`.trim();
    }
    const userMsg = { role: 'user', content: messageToSend, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setTotalCount(prev => prev + 1);
    setInput('');
    setAttachedFile(null);
    setLoading(true);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    const streamingMsg = { role: 'assistant', content: '', timestamp: new Date().toISOString(), message_id: null };
    setMessages(prev => [...prev, streamingMsg]);
    const assistantIndex = (await new Promise(resolve => {
      setMessages(prev => { resolve(prev.length - 1); return prev; });
    }));
    try {
      const response = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, message: messageToSend, companion_id: companionId, thread_id: threadId })
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let messageId = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const text = decoder.decode(value);
        const lines = text.split('\n');
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data) continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.message_id && !messageId) {
              messageId = parsed.message_id;
              setMessages(prev => prev.map((m, i) =>
                i === prev.length - 1 ? { ...m, message_id: messageId } : m
              ));
            }
            if (parsed.delta) {
              setMessages(prev => prev.map((m, i) =>
                i === prev.length - 1 ? { ...m, content: m.content + parsed.delta } : m
              ));
              const container = document.getElementById('messages-container');
              if (container) {
                const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
                if (distanceFromBottom < 100) {
                  bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
                }
              }
            }
            if (parsed.done) { setTotalCount(prev => prev + 1); }
          } catch { continue; }
        }
      }
    } catch {
      setMessages(prev => prev.map((m, i) =>
        i === prev.length - 1 ? { ...m, content: 'something went wrong 🥀' } : m
      ));
    }
    setLoading(false);
  };

  const renderInline = (text, keyPrefix) => {
    // handle **bold** and *italic* inline
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return parts.map((part, j) => {
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return <strong key={`${keyPrefix}-${j}`}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
        return <em key={`${keyPrefix}-${j}`}>{part.slice(1, -1)}</em>;
      }
      return <span key={`${keyPrefix}-${j}`}>{part}</span>;
    });
  };

  const renderText = (text) => {
    return text.split('\n').map((line, i, arr) => {
      // > blockquote
      if (line.startsWith('> ')) {
        return (
          <div key={i} style={{
            borderLeft: '2px solid rgba(201,147,90,0.4)',
            paddingLeft: '12px',
            margin: '4px 0',
            color: '#7a6a5a',
            fontStyle: 'italic',
          }}>
            {renderInline(line.slice(2), `bq-${i}`)}
          </div>
        );
      }
      // ## heading
      if (line.startsWith('## ')) {
        return (
          <div key={i} style={{ fontSize: '19px', fontWeight: '500', color: '#e8ddd0', marginBottom: '4px', marginTop: i > 0 ? '8px' : '0' }}>
            {renderInline(line.slice(3), `h2-${i}`)}
          </div>
        );
      }
      // #### heading
      if (line.startsWith('#### ')) {
        return (
          <div key={i} style={{ fontSize: '14px', fontWeight: '500', color: '#c9935a', marginBottom: '2px', marginTop: i > 0 ? '4px' : '0' }}>
            {renderInline(line.slice(5), `h4-${i}`)}
          </div>
        );
      }
      // ### heading
      if (line.startsWith('### ')) {
        return (
          <div key={i} style={{ fontSize: '16px', fontWeight: '500', color: '#c9935a', marginBottom: '2px', marginTop: i > 0 ? '6px' : '0' }}>
            {renderInline(line.slice(4), `h3-${i}`)}
          </div>
        );
      }
      // normal line
      return (
        <span key={i}>
          {renderInline(line, `line-${i}`)}
          {i < arr.length - 1 && <br />}
        </span>
      );
    });
  };

  const AudioPlayer = ({ message, index }) => {
    const progress = audioProgress[index] || 0;
    const duration = audioDurations[index] || 0;
    const pct = duration > 0 ? (progress / duration) * 100 : 0;
    const isPlaying = playingIndex === index;
    const isGenerating = generatingAudio[index];
    return (
      <div style={styles.audioPlayer}>
        <button style={styles.playPauseBtn} onClick={() => handlePlay(message, index)}>
          {isGenerating ? '⏳' : isPlaying ? '⏸' : '▶'}
        </button>
        <div style={styles.playerMiddle}>
          <div style={styles.scrubberWrap} onClick={(e) => scrub(index, e)} onTouchEnd={(e) => scrub(index, e)}>
            <div style={{...styles.scrubberFill, width: `${pct}%`}} />
            <div style={{...styles.scrubberThumb, left: `${pct}%`}} />
          </div>
          <div style={styles.timeRow}>
            <span style={styles.timeCurrent}>{formatTime(progress)}</span>
            <span style={styles.timeTotal}>{formatTime(duration)}</span>
          </div>
        </div>
        <button style={styles.shareBtn} onClick={() => shareAudio(message, index)} title="share">⬆</button>
      </div>
    );
  };

  const ContextMenu = ({ message, index }) => {
    const isHim = message.role === 'assistant';
    const isFav = message.is_favorite;
    return (
      <>
        <div style={styles.overlay} onClick={closeMenu} />
        <div style={{
          ...styles.contextMenu,
          ...(isHim ? { alignSelf: 'flex-start' } : { alignSelf: 'flex-end' })
        }}>
          {isHim && (
            <>
              <button style={styles.menuBtn} onClick={() => reroll(message, index)}>↺ reroll</button>
              <div style={styles.menuDivider} />
            </>
          )}
          <button style={styles.menuBtn} onClick={() => openFavoriteModal(message, index)}>
            {isFav ? '★ saved' : '☆ save'}
          </button>
          <div style={styles.menuDivider} />
          <button style={styles.menuBtn} onClick={() => branchThread(message, index)}>↗ branch</button>
          <div style={styles.menuDivider} />
          <button style={styles.menuBtn} onClick={() => startEdit(message, index)}>✏️ edit</button>
          <div style={styles.menuDivider} />
          <button style={{...styles.menuBtn, ...styles.menuBtnDanger}} onClick={() => deleteMessage(message, index)}>🗑 delete</button>
        </div>
      </>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate(`/threads/${companionId}`)}>←</button>
        <div style={styles.headerCenter}>
          <div style={styles.avatar}>
            {companion && (
              companion.avatar_url
                ? <img src={companion.avatar_url} alt="avatar" style={styles.avatarImg} />
                : companion.name?.[0]
            )}
          </div>
          <div style={styles.headerInfo}>
            {companion && (
              <>
                <div style={styles.headerName}>{companion.name}</div>
                <div style={styles.headerStatus}>always here ♡</div>
              </>
            )}
          </div>
        </div>
        <div style={styles.headerRight}>
          <button style={styles.headerBtn} onClick={() => navigate(`/feed/${companionId}`)}>🌸</button>
          <button style={styles.headerBtn} onClick={() => navigate(`/journal/${companionId}`)}>🕯️</button>
          <button style={styles.headerBtn} onClick={() => navigate(`/memory/${companionId}/${threadId}`)}>🧠</button>
          <button style={styles.headerBtn} onClick={() => navigate(`/library/${companionId}`)}>⭐</button>
          <button style={styles.headerBtn} onClick={() => navigate(`/chat-settings/${companionId}`)}>⚙</button>
        </div>
      </div>

      <div id="messages-container" ref={messagesContainerRef} style={styles.messages}>
        {hasMore && (
          <div style={styles.loadMoreWrap}>
            <button style={styles.loadMoreBtn} onClick={loadMoreMessages} disabled={loadingMore}>
              {loadingMore ? '…' : 'load earlier messages'}
            </button>
          </div>
        )}

        {messages.map((m, i) => {
          const showDivider = m.timestamp && shouldShowDateDivider(messages, i);
          const isEditing = editingIndex === i;
          const menuOpen = activeMenu === i;
          return (
            <React.Fragment key={i}>
              {showDivider && (
                <div style={styles.dateDivider}>
                  <div style={styles.dateLine} />
                  <div style={styles.dateText}>{formatDate(m.timestamp)}</div>
                  <div style={styles.dateLine} />
                </div>
              )}
              <div style={{...styles.msgRow, alignItems: m.role === 'user' ? 'flex-end' : 'flex-start'}}>
                {menuOpen && <ContextMenu message={m} index={i} />}
                {isEditing ? (
                  <div style={{...styles.editBox, alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start'}}>
                    <textarea
                      style={styles.editTextarea}
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      autoFocus
                    />
                    <div style={styles.editActions}>
                      <button style={styles.editCancel} onClick={() => setEditingIndex(null)}>cancel</button>
                      <button style={styles.editSave} onClick={() => saveEdit(m, i)}>save</button>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      ...(m.role === 'user' ? styles.bubbleYou : styles.bubbleHim),
                      ...(m.is_favorite ? { borderColor: 'rgba(201,147,90,0.4)' } : {})
                    }}
                    onMouseDown={() => handlePressStart(i)}
                    onMouseUp={handlePressEnd}
                    onMouseLeave={handlePressEnd}
                    onTouchStart={() => handlePressStart(i)}
                    onTouchEnd={handlePressEnd}
                  >
                    {m.is_favorite && <span style={styles.favStar}>★</span>}
                    {renderText(m.content)}
                  </div>
                )}
                {m.role === 'user' && m.timestamp && (
                  <div style={{...styles.timestamp, alignSelf: 'flex-end'}}>
                    <span style={styles.msgNum}>#{totalCount - messages.length + i + 1}</span> {formatTimestamp(m.timestamp)}
                  </div>
                )}
                {m.role === 'assistant' && (
                  <AudioPlayer message={m} index={i} />
                )}
                {m.role === 'assistant' && m.timestamp && (
                  <div style={{...styles.timestamp, alignSelf: 'flex-start'}}>
                    <span style={styles.msgNum}>#{totalCount - messages.length + i + 1}</span> {formatTimestamp(m.timestamp)}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
        {loading && (
          <div style={{...styles.msgRow, alignItems: 'flex-start'}}>
            <div style={styles.bubbleHim}>
              <div style={styles.typing}>
                <span style={styles.dot}></span>
                <span style={styles.dot}></span>
                <span style={styles.dot}></span>
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div style={styles.inputArea}>
        {attachedFile && (
          <div style={styles.fileChip}>
            <span style={styles.fileChipName}>📄 {attachedFile.name}</span>
            <button style={styles.fileChipRemove} onClick={() => setAttachedFile(null)}>✕</button>
          </div>
        )}
        <div style={styles.inputRow}>
          {companionId === FILE_ATTACH_COMPANION_ID && (
            <>
              <button style={styles.attachBtn} onClick={() => document.getElementById('fileAttachInput').click()}>📎</button>
              <input type="file" id="fileAttachInput" accept=".txt" style={{ display: 'none' }} onChange={handleFileAttach} />
            </>
          )}
          <textarea
            ref={textareaRef}
            style={styles.input}
            placeholder="say something…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            rows={1}
          />
          <button
            style={{
              ...styles.micBtn,
              ...(recording ? styles.micBtnActive : {}),
            }}
            onClick={toggleRecording}
            disabled={transcribing}
          >
            {transcribing ? (
              <span style={styles.micSpinner}>⏳</span>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" fill="currentColor"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
                <line x1="12" y1="19" x2="12" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="8" y1="23" x2="16" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
          </button>
          <button style={styles.sendBtn} onClick={() => sendMessage()}>↑</button>
        </div>
      </div>

      {favoriteModal && (
        <>
          <div style={styles.modalOverlay} onClick={() => setFavoriteModal(null)} />
          <div style={styles.modal}>
            <div style={styles.modalTitle}>
              {favoriteModal.message.is_favorite ? 'edit saved moment' : 'save this moment ★'}
            </div>
            <div style={styles.modalPreview}>
              {favoriteModal.message.content?.slice(0, 100)}
              {favoriteModal.message.content?.length > 100 ? '…' : ''}
            </div>
            <textarea
              style={styles.modalNote}
              placeholder="add a note… (optional)"
              value={favoriteNote}
              onChange={e => setFavoriteNote(e.target.value)}
            />
            <div style={styles.modalActions}>
              {favoriteModal.message.is_favorite && (
                <button style={styles.modalRemove} onClick={() => saveFavorite(false)}>remove</button>
              )}
              <button style={styles.modalCancel} onClick={() => setFavoriteModal(null)}>cancel</button>
              <button style={styles.modalSave} onClick={() => saveFavorite(true)}>save ★</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  container: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: '#0e0b0f', fontFamily: "'DM Sans', sans-serif",
    display: 'flex', flexDirection: 'column', width: '100%',
  },
  header: {
    padding: '20px 24px 16px',
    background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)',
    borderBottom: '1px solid rgba(201,147,90,0.08)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
  },
  backBtn: {
    width: '36px', height: '36px', borderRadius: '50%',
    border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    color: '#c9935a', fontSize: '16px', cursor: 'pointer',
  },
  headerCenter: { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: {
    width: '38px', height: '38px', borderRadius: '50%',
    background: 'linear-gradient(135deg, #3a2a1a, #5a3d20)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '16px', color: '#c9935a', fontStyle: 'italic', overflow: 'hidden',
  },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' },
  headerInfo: {},
  headerName: { fontSize: '16px', color: '#e8ddd0', fontWeight: '400', fontFamily: "'DM Sans', sans-serif" },
  headerStatus: { fontSize: '11px', color: '#4a3d30', fontWeight: '300', fontFamily: "'DM Sans', sans-serif" },
  headerRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  headerBtn: {
    width: '36px', height: '36px', borderRadius: '50%',
    border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    color: '#c9935a', fontSize: '16px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  messages: {
    flex: 1, overflowY: 'auto', padding: '20px 24px',
    display: 'flex', flexDirection: 'column', gap: '12px',
  },
  loadMoreWrap: { display: 'flex', justifyContent: 'center', paddingBottom: '8px' },
  loadMoreBtn: {
    background: 'transparent', border: '1px solid rgba(201,147,90,0.15)',
    borderRadius: '20px', padding: '8px 20px', color: '#4a3d30',
    fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: '300',
    cursor: 'pointer', letterSpacing: '0.04em',
  },
  dateDivider: { display: 'flex', alignItems: 'center', gap: '12px', margin: '4px 0' },
  dateLine: { flex: 1, height: '1px', background: 'rgba(201,147,90,0.1)' },
  dateText: {
    fontSize: '11px', color: '#4a3d30', fontWeight: '300',
    letterSpacing: '0.06em', whiteSpace: 'nowrap', fontFamily: "'DM Sans', sans-serif",
  },
  msgRow: { display: 'flex', flexDirection: 'column', position: 'relative' },
  timestamp: {
    fontSize: '10px', color: '#4a3d30', fontWeight: '300',
    marginTop: '3px', paddingLeft: '4px', paddingRight: '4px', fontFamily: "'DM Sans', sans-serif",
  },
  msgNum: { color: '#2a1f16', fontWeight: '300', fontSize: '10px' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50 },
  contextMenu: {
    display: 'flex', flexDirection: 'row', gap: '4px',
    background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)',
    borderRadius: '14px', padding: '6px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
    marginBottom: '6px', zIndex: 100,
  },
  menuBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '8px 14px', borderRadius: '10px', border: 'none',
    background: 'transparent', color: '#e8ddd0',
    fontFamily: "'DM Sans', sans-serif", fontSize: '13px',
    fontWeight: '300', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  menuBtnDanger: { color: '#c9935a' },
  menuDivider: { width: '1px', background: 'rgba(201,147,90,0.1)', margin: '4px 0' },
  editBox: { display: 'flex', flexDirection: 'column', gap: '6px', maxWidth: '90%', width: '100%' },
  editTextarea: {
    background: '#1a1520', border: '1px solid rgba(201,147,90,0.2)',
    borderRadius: '14px', padding: '12px 16px', color: '#e8ddd0',
    fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: '300',
    lineHeight: '1.6', resize: 'none', outline: 'none', width: '100%', minHeight: '80px',
  },
  editActions: { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  editCancel: {
    padding: '8px 16px', borderRadius: '10px',
    border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    color: '#4a3d30', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer',
  },
  editSave: {
    padding: '8px 16px', borderRadius: '10px', border: 'none',
    background: 'linear-gradient(135deg, #8a5f35, #c9935a)',
    color: '#0e0b0f', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer',
  },
  bubbleHim: {
    maxWidth: '90%', padding: '14px 18px', borderRadius: '20px',
    borderBottomLeftRadius: '6px', background: '#1e1825', color: '#e8ddd0',
    fontSize: '17px', lineHeight: '1.7', border: '1px solid rgba(201,147,90,0.06)',
    fontFamily: "'DM Sans', sans-serif", fontWeight: '300', cursor: 'default',
    WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none', position: 'relative',
  },
  bubbleYou: {
    maxWidth: '90%', padding: '14px 18px', borderRadius: '20px',
    borderBottomRightRadius: '6px', background: '#2a1f16', color: '#e8ddd0',
    fontSize: '17px', lineHeight: '1.7', border: '1px solid rgba(201,147,90,0.1)',
    fontFamily: "'DM Sans', sans-serif", fontWeight: '300', cursor: 'default',
    WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none', position: 'relative',
  },
  favStar: { position: 'absolute', top: '8px', right: '10px', fontSize: '10px', color: '#c9935a', opacity: 0.7 },
  audioPlayer: {
    marginTop: '8px', background: '#1a1520', borderRadius: '16px',
    border: '1px solid rgba(201,147,90,0.1)', padding: '10px 14px',
    display: 'flex', alignItems: 'center', gap: '10px', maxWidth: '90%', minWidth: '220px',
  },
  playPauseBtn: {
    width: '32px', height: '32px', borderRadius: '50%', border: 'none',
    background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f',
    fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 8px rgba(201,147,90,0.3)',
  },
  playerMiddle: { flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' },
  scrubberWrap: { position: 'relative', height: '4px', background: 'rgba(201,147,90,0.15)', borderRadius: '2px', cursor: 'pointer' },
  scrubberFill: { height: '100%', background: 'linear-gradient(90deg, #8a5f35, #c9935a)', borderRadius: '2px', transition: 'width 0.1s' },
  scrubberThumb: {
    position: 'absolute', width: '10px', height: '10px', borderRadius: '50%',
    background: '#c9935a', top: '50%', transform: 'translate(-50%, -50%)',
    boxShadow: '0 0 4px rgba(201,147,90,0.5)', transition: 'left 0.1s',
  },
  timeRow: { display: 'flex', justifyContent: 'space-between' },
  timeCurrent: { fontSize: '10px', color: '#c9935a', fontWeight: '300', fontFamily: "'DM Sans', sans-serif" },
  timeTotal: { fontSize: '10px', color: '#4a3d30', fontWeight: '300', fontFamily: "'DM Sans', sans-serif" },
  shareBtn: {
    width: '28px', height: '28px', borderRadius: '50%',
    border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    color: '#4a3d30', fontSize: '11px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  typing: { display: 'flex', gap: '4px', alignItems: 'center' },
  dot: { width: '6px', height: '6px', borderRadius: '50%', background: '#8a5f35', display: 'inline-block' },
  inputArea: { padding: '16px 24px 32px', borderTop: '1px solid rgba(201,147,90,0.06)', flexShrink: 0 },
  inputRow: {
    display: 'flex', alignItems: 'flex-end', gap: '10px', background: '#1a1520',
    borderRadius: '24px', padding: '8px', border: '1px solid rgba(201,147,90,0.1)',
  },
  attachBtn: {
    width: '38px', height: '38px', borderRadius: '50%', border: 'none',
    background: 'transparent', color: '#8a5f35', fontSize: '16px', cursor: 'pointer',
    flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  fileChip: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: '#1e1825', border: '1px solid rgba(201,147,90,0.12)',
    borderRadius: '14px', padding: '8px 14px', marginBottom: '8px',
  },
  fileChipName: { fontSize: '12px', color: '#c9935a', fontWeight: '300', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileChipRemove: { background: 'transparent', border: 'none', color: '#4a3d30', fontSize: '12px', cursor: 'pointer', flexShrink: 0, marginLeft: '10px' },
  input: {
    flex: 1, border: 'none', background: 'transparent',
    fontFamily: "'DM Sans', sans-serif", fontSize: '17px', color: '#e8ddd0', outline: 'none',
    paddingLeft: '10px', paddingTop: '8px', paddingBottom: '8px',
    resize: 'none', overflowY: 'auto', maxHeight: '140px',
    lineHeight: '1.4', fontWeight: '300',
  },
  sendBtn: {
    width: '38px', height: '38px', borderRadius: '50%', border: 'none',
    background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f',
    fontSize: '16px', fontWeight: '600', cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(201,147,90,0.3)', flexShrink: 0,
  },
  micBtn: {
    width: '38px', height: '38px', borderRadius: '50%', border: 'none',
    background: 'transparent', color: '#8a5f35', cursor: 'pointer',
    flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  micBtnActive: {
    background: 'rgba(201,147,90,0.2)', color: '#c9935a',
  },
  micSpinner: { fontSize: '14px' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200 },
  modal: {
    position: 'fixed', bottom: 0, left: 0, right: 0,
    background: '#1e1825', borderRadius: '24px 24px 0 0',
    border: '1px solid rgba(201,147,90,0.15)',
    padding: '28px 24px 40px', zIndex: 201,
    display: 'flex', flexDirection: 'column', gap: '16px',
  },
  modalTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: '#e8ddd0', fontWeight: '300', fontStyle: 'italic' },
  modalPreview: {
    fontSize: '13px', color: '#7a6a5a', fontWeight: '300', lineHeight: '1.5', fontStyle: 'italic',
    background: '#0e0b0f', borderRadius: '12px', padding: '12px 14px',
  },
  modalNote: {
    background: '#0e0b0f', borderRadius: '14px', border: '1px solid rgba(201,147,90,0.1)',
    padding: '14px 16px', color: '#e8ddd0', fontFamily: "'DM Sans', sans-serif",
    fontSize: '14px', fontWeight: '300', outline: 'none', resize: 'none', minHeight: '80px', lineHeight: '1.6',
  },
  modalActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
  modalRemove: {
    padding: '10px 16px', borderRadius: '12px', border: '1px solid rgba(201,147,90,0.15)',
    background: 'transparent', color: '#4a3d30', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer',
  },
  modalCancel: {
    padding: '10px 16px', borderRadius: '12px', border: '1px solid rgba(201,147,90,0.15)',
    background: 'transparent', color: '#4a3d30', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer',
  },
  modalSave: {
    padding: '10px 20px', borderRadius: '12px', border: 'none',
    background: 'linear-gradient(135deg, #8a5f35, #c9935a)',
    color: '#0e0b0f', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer',
  },
};

export default Chat;
